import { useEffect, useRef } from 'preact/hooks';
import { COSTS } from '../engine';
import { HOUSE_RULE_INFO, ResourceList } from './widgets';

/** A one-screen rules summary, including this app's choices where the rules are ambiguous. */
export function RulesSheet({ onClose }: { onClose: () => void }) {
  // Ref keeps the keydown listener stable across re-renders of the host (which
  // creates a fresh onClose each tick when a turn-timer clock is ticking); if
  // the listener were re-added on every render there'd be a brief window with
  // no handler attached, and a keypress landing in it would be lost.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Focus moves into the dialog, stays there while it's open, and goes back where it was afterwards.
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('[data-testid="close-rules"]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onCloseRef.current();
      if (e.key !== 'Tab' || !panel.current) return;
      const focusable = [...panel.current.querySelectorAll<HTMLElement>('button, [href], [tabindex]:not([tabindex="-1"])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!panel.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);

  return (
    <div class="overlay" onClick={onClose}>
      <div
        class="panel rules"
        data-testid="rules"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rules-title"
        ref={panel}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="row between">
          <h2 id="rules-title">How to play</h2>
          <button class="small secondary" data-testid="close-rules" onClick={onClose}>
            Close
          </button>
        </div>

        <h3>Goal</h3>
        <p>First to 10 victory points (or the target the host sets) on their own turn wins.</p>
        <ul>
          <li>Settlement: 1 point. City: 2 points.</li>
          <li>Longest Road (5+ connected roads): 2 points.</li>
          <li>Largest Army (3+ knights played): 2 points.</li>
          <li>Victory Point cards: 1 point each, kept secret until the game ends (unless the reveal house rule is on).</li>
        </ul>

        <h3>Setup</h3>
        <p>
          Each player places a settlement and a road, then the same again in reverse order. Your second settlement
          collects one of each resource around it.
        </p>

        <h3>Your turn</h3>
        <ol>
          <li>Roll the dice. Every hex with that number pays its resource: 1 per settlement and 2 per city touching it.</li>
          <li>Trade with the bank (4:1, or better at a harbor) or offer trades to other players.</li>
          <li>Build, buy development cards, then end your turn.</li>
        </ol>

        <h3>Costs</h3>
        <div class="costs">
          <div>Road: <ResourceList resources={COSTS.road} /></div>
          <div>Settlement: <ResourceList resources={COSTS.settlement} /></div>
          <div>City: <ResourceList resources={COSTS.city} /></div>
          <div>Development card: <ResourceList resources={COSTS.devCard} /></div>
        </div>

        <h3>Placing pieces</h3>
        <ul>
          <li>Settlements need at least two road lengths between them (no neighbors).</li>
          <li>After setup, new settlements and roads must connect to your own roads.</li>
          <li>Cities replace one of your settlements.</li>
          <li>Limits: 15 roads, 5 settlements, 4 cities each.</li>
        </ul>

        <h3>Rolling a 7</h3>
        <ul>
          <li>Anyone holding more than 7 cards discards half, rounded down.</li>
          <li>The roller moves the robber to another hex. That hex stops producing.</li>
          <li>The roller steals a random card from a player with a building on it.</li>
        </ul>

        <h3>Development cards</h3>
        <ul>
          <li>Knight: move the robber and steal.</li>
          <li>Road Building: place two free roads.</li>
          <li>Year of Plenty: take any two resources from the bank.</li>
          <li>Monopoly: name a resource; everyone gives you all of theirs.</li>
          <li>Victory Point: worth 1 point while in your hand.</li>
          <li>Play at most one per turn, never on the turn you bought it. You may play one before rolling.</li>
        </ul>

        <h3>Optional house rules</h3>
        <p>Chosen when the game is set up (online, by the host before starting). The Players panel shows which are on.</p>
        <ul>
          {HOUSE_RULE_INFO.map((r) => (
            <li key={r.key}>
              <strong>{r.label}:</strong> {r.detail}
            </li>
          ))}
        </ul>

        <h3>Bigger games</h3>
        <p>
          With 5 or 6 players the island grows to 30 hexes with more harbors, a bigger bank, and a bigger development
          deck. Every two players after that adds another hex to each row.
        </p>

        <h3>How this app plays</h3>
        <ul>
          <li>If only one player can be robbed, the steal happens automatically.</li>
          <li>Longest Road stays with its holder on a tie. If it's broken and the rest tie, nobody holds it.</li>
          <li>If the bank can't pay everyone a resource, nobody gets it (unless only one player is owed).</li>
          <li>The host can undo the last move for misclicks. Undoing a roll and rolling again gives the same dice.</li>
          <li>
            If someone's phone dies, the host can open their seat, and they can pick it back up from any device by
            opening the room link. If the host is away, the next player in turn order acts as host.
          </li>
        </ul>
      </div>
    </div>
  );
}
